/* eslint-disable react/require-default-props */
import { MenuItem } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useUpgradePlanModal from 'components/domain/property/modal/upgradePlan/useUpgradePlanModal';
import useFetchHotels from 'components/domain/property/useFetchHotels';
import usePropertiesCreationAllowed from 'components/domain/property/usePropertiesCreationAllowed';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useAppUser from 'hooks/useAppUser';
import { PropertyBase, PropertyBusinessType } from 'models/Properties';
import { API_MAX_LIMIT } from 'services/API.constants';
import useAddSubUnitsModal from '../actions/addSubUnits/useAddSubUnitsModal';
import useConvertToUnitTypeModal from '../actions/convertToUnitType/useConvertToUnitTypeModal';
import useLinkSubUnitsModal from '../actions/linkSubUnits/useLinkSubUnitsModal';
import useDeactivateModal from '../../../components/domain/property/modal/confirmation/useDeactivateModal';
import useActivateModal from '../actions/confirmation/useActivateModal';
import useDeleteModal from '../actions/confirmation/useDeleteModal';
import useUnlinkSubUnitModal from '../actions/confirmation/useUnlinkSubUnitModal';
import useDuplicateAction from '../actions/useDuplicateAction';
import usePropertyAddUnitTypePath from '../../property/usePropertyAddUnitTypePath';
import { PropertyActionsPlaceholderStyled } from './PropertyListItem.styles';
import PropertyActionsDropdown from './PropertyActionsDropdown';

interface PropertyActionsProps {
  parentProperty?: Pick<PropertyBase, 'businessType' | 'uid' | 'name'>;
  property: Pick<PropertyBase, 'businessType' | 'uid' | 'name'> &
    Partial<Pick<PropertyBase, 'isActive' | 'subUnits'>>;
}

const PropertyActions = ({
  parentProperty,
  property,
}: PropertyActionsProps) => {
  const { t } = useTranslation();
  const { isEmployeeManager } = useAppUser();
  const { data: hotels, isLoading: isFetchingHotels } = useFetchHotels({
    limit: API_MAX_LIMIT,
  });
  const { canAddProperties, openUpgradePlanModal } = useUpgradePlanModal();
  const { openAddSubUnitsModal } = useAddSubUnitsModal();
  const { openConvertToUnitTypeModal } = useConvertToUnitTypeModal();
  const { openLinkSubUnitsModal } = useLinkSubUnitsModal();
  const { openDeactivateModal } = useDeactivateModal();
  const { openActivateModal } = useActivateModal();
  const { openDeleteModal } = useDeleteModal();
  const { openUnlinkSubUnitModal } = useUnlinkSubUnitModal();
  const { handleDuplicate, isDuplicating } = useDuplicateAction();
  const canCreateProperties = usePropertiesCreationAllowed();
  const getPropertyAddUnitTypePath = usePropertyAddUnitTypePath();

  const { uid: propertyUid, name, businessType } = property;
  const isHotel = businessType === PropertyBusinessType.HOTEL;
  const isMasterUnit = (property.subUnits ?? []).length > 0;
  const isSubUnit = businessType === PropertyBusinessType.SUB_UNIT;
  const isUnitType = businessType === PropertyBusinessType.UNIT_TYPE;
  const isUnit = businessType === PropertyBusinessType.UNIT;
  const hasHotels = !!hotels?.length;

  const handleAddSubUnitsClick = () => openAddSubUnitsModal({ propertyUid });

  const handleConvertToUnitTypeClick = () =>
    openConvertToUnitTypeModal({ propertyUid });

  const handleLinkSubUnitsClick = () => openLinkSubUnitsModal({ propertyUid });

  const handleDeactiveClick = () =>
    openDeactivateModal({ uid: propertyUid, name });

  const handleActivateClick = () =>
    openActivateModal({ uid: propertyUid, name });

  const handleDeleteClick = () =>
    openDeleteModal({
      uid: propertyUid,
      name,
      isHotel,
      isMasterUnit,
      isUnitType,
    });

  const handleUnlinkSubUnitClick = () => {
    if (parentProperty) {
      openUnlinkSubUnitModal({ masterUnit: parentProperty, subUnit: property });
    } else {
      throw new Error('No parent property found.');
    }
  };

  const handleDuplicateClick: React.MouseEventHandler = (event) => {
    if (!canAddProperties(1)) {
      event.preventDefault();
      openUpgradePlanModal();
    } else {
      handleDuplicate({ uid: propertyUid, name });
    }
  };

  if (isFetchingHotels) {
    return <LoadingPlaceholder width="26px" linesCount={1.3} />;
  }

  if (isUnit) {
    return <PropertyActionsPlaceholderStyled />;
  }

  if (isUnitType) {
    return canCreateProperties ? (
      <PropertyActionsDropdown propertyUid={property.uid}>
        <MenuItem
          data-testid="property-actions-menu-item"
          onClick={handleDeleteClick}
        >
          {t('pageProperties.actions.delete')}
        </MenuItem>
      </PropertyActionsDropdown>
    ) : (
      <PropertyActionsPlaceholderStyled />
    );
  }

  return (
    <PropertyActionsDropdown propertyUid={property.uid}>
      {property.isActive && (
        <>
          {canCreateProperties && !isHotel && !isSubUnit && (
            <MenuItem
              data-testid="property-actions-menu-item"
              onClick={handleDuplicateClick}
              disabled={isDuplicating}
            >
              {t('pageProperties.actions.duplicate')}
            </MenuItem>
          )}
          {canCreateProperties && !isHotel && !isSubUnit && (
            <MenuItem
              data-testid="property-actions-menu-item"
              onClick={handleAddSubUnitsClick}
            >
              {t('pageProperties.actions.addSubUnits')}
            </MenuItem>
          )}
          {!isHotel && !isSubUnit && (
            <MenuItem
              data-testid="property-actions-menu-item"
              onClick={handleLinkSubUnitsClick}
            >
              {t('pageProperties.actions.linkSubUnits')}
            </MenuItem>
          )}
          {canCreateProperties && hasHotels && !isHotel && !isMasterUnit && (
            <MenuItem
              data-testid="property-actions-menu-item"
              onClick={handleConvertToUnitTypeClick}
            >
              {t('pageProperties.actions.convertToUnitType')}
            </MenuItem>
          )}
          {canCreateProperties && isHotel && (
            <MenuItem
              data-testid="property-actions-menu-item"
              href={getPropertyAddUnitTypePath(property)}
            >
              {t('pageProperties.actions.createUnitType')}
            </MenuItem>
          )}
          {canCreateProperties && !isHotel && !isSubUnit && (
            <MenuItem
              data-testid="property-actions-menu-item"
              onClick={handleDeactiveClick}
            >
              {t('pageProperties.actions.deactivate')}
            </MenuItem>
          )}
        </>
      )}
      {isEmployeeManager && isSubUnit && (
        <MenuItem
          data-testid="property-actions-menu-item"
          onClick={handleUnlinkSubUnitClick}
        >
          {t('pageProperties.actions.unlinkSubUnit')}
        </MenuItem>
      )}
      {canCreateProperties && !property.isActive && !isHotel && !isSubUnit && (
        <MenuItem
          data-testid="property-actions-menu-item"
          onClick={handleActivateClick}
        >
          {t('pageProperties.actions.reactivate')}
        </MenuItem>
      )}
      {canCreateProperties && <MenuItem divider />}
      {canCreateProperties && (
        <MenuItem
          data-testid="property-actions-menu-item"
          onClick={handleDeleteClick}
        >
          {t('pageProperties.actions.delete')}
        </MenuItem>
      )}
    </PropertyActionsDropdown>
  );
};

export default PropertyActions;
